import { useRef, type FC, useState } from 'react';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { VideoPlayer } from 'dibs-video/exports/VideoPlayer.ts';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { TradeBenefitsTestimonialCarousel } from './TradeBenefitsTestimonialCarousel.tsx';

import { buildMuxUrl } from 'dibs-video/exports/helpers.ts';
import { useServerVars } from '../../shared/ServerVarsContext.tsx';
import { trackTradeBenefitsEvent } from '../tradeBenefitsTracking.ts';
import { BASE_IMAGE_URL } from '../tradeBenefitsConstants.ts';

import sharedStyles from '../TradeBenefitsSharedStyles.scss';
import styles from './TradeBenefitsTestimonial.scss';
import dibsCss from 'dibs-css';

const VideoPosterFallback: FC<{ playbackId: string }> = ({ playbackId }) => (
    <img
        src={`https://image.mux.com/${playbackId}/thumbnail.webp`}
        alt=""
        loading="lazy"
        decoding="async"
        className={classnames(dibsCss.wFull, dibsCss.hFull, dibsCss.objectCover)}
    />
);

const TradeBenefitsTestimonial: FC = () => {
    const playerContainerRef = useRef<HTMLVideoElement>(null);
    const { isMobile, tradeBenefitsVideoPlaybackId } = useServerVars();
    const playbackId = tradeBenefitsVideoPlaybackId || '';
    const [showPlayer, setShowPlayer] = useState(false);

    return (
        <div
            className={classnames(
                styles.container,
                sharedStyles.fullWidthContainer,
                dibsCss.pXlarge
            )}
        >
            <div
                className={classnames(
                    styles.content,
                    dibsCss.maxW1440px,
                    dibsCss.my0,
                    dibsCss.mxAuto,
                    dibsCss.grid,
                    dibsCss.gapXlarge,
                    dibsCss.gapYLarger
                )}
            >
                <div className={styles.header}>
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={classnames(
                                    sharedStyles.headingLarge,
                                    styles.heading,
                                    dibsCss.mt0,
                                    dibsCss.mbMedium
                                )}
                            >
                                <FormattedMessage
                                    id="abt.TradeBenefitsTestimonial.title"
                                    defaultMessage="Hear from Our{br}<italic>Design Community</italic>"
                                    values={{
                                        br: <br />,
                                        italic: content => (
                                            <span
                                                className={classnames(
                                                    sharedStyles.headingLargeItalic,
                                                    dibsCss.textHeaderBannerSmallItalic
                                                )}
                                            >
                                                {content}
                                            </span>
                                        ),
                                    }}
                                />
                            </Heading>
                        )}
                    </HeadingLevel>
                    <div className={classnames(styles.description, dibsCss.sassyFontBody)}>
                        <FormattedMessage
                            id="abt.TradeBenefitsTestimonial.description"
                            defaultMessage="Hear how the 1stDibs Trade 1st Program supports designers and helps them to find exceptional pieces, streamline their sourcing process, and deliver extraordinary results for their clients."
                        />
                    </div>
                </div>
                <div className={styles.carousel}>
                    <TradeBenefitsTestimonialCarousel />
                </div>
                <div className={styles.video}>
                    {showPlayer ? (
                        <ClientSuspense fallback={<VideoPosterFallback playbackId={playbackId} />}>
                            <VideoPlayer forceMute={false} ref={playerContainerRef}>
                                <VideoPlayer.Media
                                    src={buildMuxUrl({ playbackId })}
                                    playback={{
                                        autoPlay: true,
                                        loop: false,
                                        muted: true,
                                        playsInline: !isMobile,
                                    }}
                                    callbacks={{
                                        onPlay: () =>
                                            trackTradeBenefitsEvent({
                                                stepInteractionName: 'video play clicked',
                                                trigger: 'testimonials',
                                            }),
                                    }}
                                />
                                <VideoPlayer.Controls />
                            </VideoPlayer>
                        </ClientSuspense>
                    ) : (
                        <Link onClick={() => setShowPlayer(true)}>
                            <img
                                src={`${BASE_IMAGE_URL}/testimonial_video_still_playbutton.jpg`}
                                alt=""
                                className={classnames(
                                    dibsCss.wFull,
                                    dibsCss.hFull,
                                    dibsCss.objectCover
                                )}
                            />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

export { TradeBenefitsTestimonial };
